import { useRef, useState } from "react";
import Table from "react-bootstrap/Table";
import Pagination from "react-bootstrap/Pagination";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import moment from "moment";
import { Copy, ExternalLink, FileText, Info, Upload, X } from "lucide-react";
import { toast } from "react-toastify";
import {
  issueInsuranceCasePolicy,
  resolveInsuranceCoverageReview,
} from "../../../context/functions/AdminFunction";

const PRODUCT_BADGE = {
  insurance: { label: "Insurance Project", className: "product-badge-insurance" },
  skor_protect: { label: "Skor Protect", className: "product-badge-skor-protect" },
};

// Row statuses are computed by the backend (utils/adminInsuranceRows.js).
const PAYMENT_BADGE = {
  paid: { label: "Paid", className: "status-badge-active" },
  awaiting_payment: { label: "Awaiting payment", className: "ip-badge-neutral" },
  expired: { label: "Expired", className: "ip-badge-neutral" },
  refunded: { label: "Refunded", className: "ip-badge-neutral" },
};

const INSURANCE_STATUS_BADGE = {
  under_review: { label: "Under review", className: "ip-badge-warning" },
  pending_contract: { label: "Pending contract", className: "ip-badge-warning" },
  active: { label: "Active", className: "status-badge-active" },
  rejected: { label: "Rejected", className: "status-badge-deleted" },
  awaiting_payment: { label: "Awaiting payment", className: "ip-badge-neutral" },
  not_started: { label: "Not started", className: "ip-badge-neutral" },
  failed: { label: "Issuing failed", className: "status-badge-deleted" },
  processing: { label: "Issuing", className: "insurance-payment-badge direct-debit" },
  expired: { label: "Expired", className: "ip-badge-neutral" },
  coverage_review: { label: "Coverage review", className: "status-badge-expired" },
};

// Every case action asks for confirmation before it runs.
const CONFIRM_ACTIONS = {
  reject: {
    title: "Reject this case?",
    message:
      "The landlord is emailed that the insurer can't cover this rental and that the premium will be refunded. The property stays awarded, without insurance. This cannot be undone.",
    confirmLabel: "Reject case",
    danger: true,
  },
  refund: {
    title: "Mark the premium as refunded?",
    message:
      "Only do this once the refund has been made in the payment gateway. The payer receives the refund completed email.",
    confirmLabel: "Mark refunded",
  },
  "retry-issuance": {
    title: "Retry issuance?",
    message:
      "Retries issuing the policy with MDP. On success the landlord gets the policy issued email and the card shows Active.",
    confirmLabel: "Retry issuance",
  },
  "send-reminder": {
    title: "Send a reminder?",
    message:
      "The landlord receives an email and an in-app notification to add the beneficiary and upload the contract.",
    confirmLabel: "Send reminder",
  },
};

const ConfirmActionModal = ({ pending, onCancel, onConfirm }) => {
  const config = pending ? CONFIRM_ACTIONS[pending.action] : null;
  const propertyLabel = [
    pending?.item?.property?.street,
    pending?.item?.property?.street_number,
    pending?.item?.property?.muncipality,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Modal
      show={Boolean(config)}
      onHide={onCancel}
      centered
      className="ip-confirm-modal"
      backdropClassName="ip-confirm-backdrop"
    >
      <Modal.Header closeButton>
        <Modal.Title as="h5">{config?.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {propertyLabel && <p className="fw-600 mb-2">{propertyLabel}</p>}
        <p className="text-muted mb-0">{config?.message}</p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant={config?.danger ? "danger" : undefined}
          className={config?.danger ? undefined : "coverage-review-submit-btn"}
          onClick={onConfirm}
        >
          {config?.confirmLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const formatDate = (date) => (date ? moment(date).format("DD MMM YYYY") : null);

export const CoverageReviewForm = ({
  item,
  landlordName,
  onCancel,
  onResolved,
}) => {
  const initialStartDate = item.policy_dates?.start
    ? moment(item.policy_dates.start)
    : moment();
  const [startDate, setStartDate] = useState(
    initialStartDate.format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(
    item.policy_dates?.end
      ? moment(item.policy_dates.end).format("YYYY-MM-DD")
      : initialStartDate.clone().add(1, "year").format("YYYY-MM-DD")
  );
  const [policyDocument, setPolicyDocument] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const currentPolicyUrl = item.policy_doc || null;
  const currentPolicyName = getPolicyFileName(currentPolicyUrl);

  const propertyLabel = [item.property?.street, item.property?.street_number]
    .filter(Boolean)
    .join(" ");
  const summaryRows = [
    {
      label: "Property",
      value: [propertyLabel, item.property?.muncipality]
        .filter(Boolean)
        .join(", "),
    },
    { label: "Landlord", value: landlordName },
  ];

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("The policy must be a PDF");
      return;
    }
    setPolicyDocument(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!startDate || !endDate || (!policyDocument && !currentPolicyUrl)) {
      toast.error("Start date, end date, and a PDF policy are required");
      return;
    }
    if (moment(endDate).isBefore(moment(startDate), "day")) {
      toast.error("Policy end date cannot be before the start date");
      return;
    }

    setSaving(true);
    try {
      await resolveInsuranceCoverageReview({
        propertyId: item.property?._id,
        policyStartDate: startDate,
        policyEndDate: endDate,
        policyDocument,
      });
      toast.success("Policy updated and coverage returned to Active");
      await onResolved?.();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to update policy"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit} className="ip-upload-policy">
      <div className="ip-upload-policy-header">
        <div>
          <h4>Update policy</h4>
          <p>
            Update the coverage period and, if needed, replace the policy PDF.
            Saving returns the coverage to Active.
          </p>
        </div>
        <button
          type="button"
          className="ip-upload-policy-close"
          onClick={onCancel}
          disabled={saving}
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>

      <div className="ip-upload-policy-summary">
        {summaryRows.map((row) => (
          <div key={row.label} className="ip-upload-policy-summary-row">
            <span>{row.label}</span>
            <strong>{row.value || "—"}</strong>
          </div>
        ))}
      </div>

      <div className="ip-upload-policy-section">Policy</div>

      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="d-none"
        onChange={handleFileChange}
      />
      {policyDocument || currentPolicyUrl ? (
        <div className="ip-upload-policy-file">
          <span className="ip-upload-policy-file-icon">
            <FileText size={20} />
          </span>
          <div className="ip-upload-policy-file-info">
            {policyDocument ? (
              <>
                <strong title={policyDocument.name}>
                  {policyDocument.name}
                </strong>
                <span>Policy PDF · {formatFileSize(policyDocument.size)}</span>
              </>
            ) : (
              <>
                <strong title={currentPolicyName}>
                  <a
                    href={currentPolicyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ip-upload-policy-file-link"
                  >
                    {currentPolicyName}
                  </a>
                </strong>
                <span>Policy PDF</span>
              </>
            )}
          </div>
          <button
            type="button"
            className="ip-upload-policy-replace"
            disabled={saving}
            onClick={() => fileInputRef.current?.click()}
          >
            Replace
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="ip-upload-policy-file ip-upload-policy-file--empty"
          disabled={saving}
          onClick={() => fileInputRef.current?.click()}
        >
          <span className="ip-upload-policy-file-icon">
            <Upload size={20} />
          </span>
          <div className="ip-upload-policy-file-info">
            <strong>Choose the policy PDF</strong>
            <span>PDF only</span>
          </div>
        </button>
      )}

      <div className="ip-upload-policy-dates">
        <label className="ip-upload-policy-field">
          <span>
            Start date <em>*</em>
          </span>
          <input
            type="date"
            value={startDate}
            disabled={saving}
            onChange={(event) => setStartDate(event.target.value)}
          />
        </label>
        <label className="ip-upload-policy-field">
          <span>
            End date <em>*</em>
          </span>
          <input
            type="date"
            min={startDate || undefined}
            value={endDate}
            disabled={saving}
            onChange={(event) => setEndDate(event.target.value)}
          />
        </label>
      </div>

      <div className="ip-upload-policy-note">
        <Info size={18} />
        <p>
          The landlord sees the new policy and coverage period as Active in the
          property panel after saving.
        </p>
      </div>

      <div className="ip-upload-policy-actions">
        <button
          type="button"
          className="ip-upload-policy-btn"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="ip-upload-policy-btn ip-upload-policy-btn--primary"
          disabled={saving}
        >
          {saving ? "Saving…" : "Save and activate"}
        </button>
      </div>
    </Form>
  );
};

// Uploaded blobs are named "<name>__<uuid>.pdf"; show "<name>.pdf".
const getPolicyFileName = (url) => {
  if (!url) return null;
  try {
    const name = decodeURIComponent(new URL(url).pathname.split("/").pop());
    return name.replace(/__[0-9a-f-]{36}(?=\.[^.]+$)/i, "") || "Policy.pdf";
  } catch {
    return "Policy.pdf";
  }
};

const formatFileSize = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(Math.round(bytes / 1024), 1)} KB`;

// A policy runs one year: 15/10/2026 → 14/10/2027.
const getDefaultPolicyEnd = (start) =>
  start
    ? moment(start).add(1, "year").subtract(1, "day").format("YYYY-MM-DD")
    : "";

// Ops issued the policy in MDP by hand and uploads it here. The dates start
// from what the landlord chose.
export const IssuePolicyForm = ({ item, landlordName, onCancel, onIssued }) => {
  const requestedStart = item.policy_dates?.start
    ? moment(item.policy_dates.start)
    : null;
  const [policyNumber, setPolicyNumber] = useState("");
  const [startDate, setStartDate] = useState(
    requestedStart ? requestedStart.format("YYYY-MM-DD") : ""
  );
  const [endDate, setEndDate] = useState(() =>
    getDefaultPolicyEnd(requestedStart)
  );
  const [policyDocument, setPolicyDocument] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const propertyLabel = [item.property?.street, item.property?.street_number]
    .filter(Boolean)
    .join(" ");
  const beneficiary = item.beneficiary || {};
  const beneficiaryLabel =
    beneficiary.label === "Not set yet"
      ? "Not set yet"
      : [beneficiary.name, beneficiary.document].filter(Boolean).join(" · ") ||
        "—";
  const summaryRows = [
    {
      label: "Property",
      value: [propertyLabel, item.property?.muncipality]
        .filter(Boolean)
        .join(", "),
    },
    { label: "Landlord", value: landlordName },
    { label: "Beneficiary", value: beneficiaryLabel },
    {
      label: "Requested start",
      value: requestedStart
        ? requestedStart.format("DD MMM YYYY")
        : "Not set yet",
    },
  ];

  const handleStartChange = (value) => {
    setStartDate(value);
    // Keep the one-year period unless Ops changes the end date afterwards.
    setEndDate(getDefaultPolicyEnd(value));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("The policy must be a PDF");
      return;
    }
    setPolicyDocument(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!policyNumber.trim() || !startDate || !endDate || !policyDocument) {
      toast.error("Policy number, dates and a PDF policy are required");
      return;
    }
    if (moment(endDate).isBefore(moment(startDate), "day")) {
      toast.error("Policy end date cannot be before the start date");
      return;
    }

    setSaving(true);
    try {
      await issueInsuranceCasePolicy({
        propertyId: item.property?._id,
        policyNumber: policyNumber.trim(),
        policyStartDate: startDate,
        policyEndDate: endDate,
        policyDocument,
      });
      toast.success("Policy uploaded and insurance set to Active");
      await onIssued?.();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to upload policy"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit} className="ip-upload-policy">
      <div className="ip-upload-policy-header">
        <div>
          <h4>Upload policy</h4>
          <p>
            Issue the policy in the MDP web app first. Saving sets the policy
            to Active and emails it to the landlord.
          </p>
        </div>
        <button
          type="button"
          className="ip-upload-policy-close"
          onClick={onCancel}
          disabled={saving}
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>

      <div className="ip-upload-policy-summary">
        {summaryRows.map((row) => (
          <div key={row.label} className="ip-upload-policy-summary-row">
            <span>{row.label}</span>
            <strong>{row.value || "—"}</strong>
          </div>
        ))}
      </div>

      <div className="ip-upload-policy-section">Policy issued in MDP</div>

      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="d-none"
        onChange={handleFileChange}
      />
      {policyDocument ? (
        <div className="ip-upload-policy-file">
          <span className="ip-upload-policy-file-icon">
            <FileText size={20} />
          </span>
          <div className="ip-upload-policy-file-info">
            <strong title={policyDocument.name}>{policyDocument.name}</strong>
            <span>Policy PDF · {formatFileSize(policyDocument.size)}</span>
          </div>
          <button
            type="button"
            className="ip-upload-policy-replace"
            disabled={saving}
            onClick={() => fileInputRef.current?.click()}
          >
            Replace
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="ip-upload-policy-file ip-upload-policy-file--empty"
          disabled={saving}
          onClick={() => fileInputRef.current?.click()}
        >
          <span className="ip-upload-policy-file-icon">
            <Upload size={20} />
          </span>
          <div className="ip-upload-policy-file-info">
            <strong>Choose the policy PDF</strong>
            <span>PDF only</span>
          </div>
        </button>
      )}

      <label className="ip-upload-policy-field">
        <span>
          Policy number <em>*</em>
        </span>
        <input
          type="text"
          value={policyNumber}
          disabled={saving}
          onChange={(event) => setPolicyNumber(event.target.value)}
        />
      </label>
      <div className="ip-upload-policy-dates">
        <label className="ip-upload-policy-field">
          <span>
            Start date <em>*</em>
          </span>
          <input
            type="date"
            value={startDate}
            disabled={saving}
            onChange={(event) => handleStartChange(event.target.value)}
          />
        </label>
        <label className="ip-upload-policy-field">
          <span>
            End date <em>*</em>
          </span>
          <input
            type="date"
            min={startDate || undefined}
            value={endDate}
            disabled={saving}
            onChange={(event) => setEndDate(event.target.value)}
          />
        </label>
      </div>

      <div className="ip-upload-policy-note">
        <Info size={18} />
        <p>
          The landlord sees the policy in the property panel and gets the policy
          issued email with this PDF. The tenant never sees it.
        </p>
      </div>

      <div className="ip-upload-policy-actions">
        <button
          type="button"
          className="ip-upload-policy-btn"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="ip-upload-policy-btn ip-upload-policy-btn--primary"
          disabled={saving}
        >
          {saving ? "Uploading…" : "Upload policy"}
        </button>
      </div>
    </Form>
  );
};

// One link per document that exists. The invoice is linked under Payment.
const DocLinks = ({ item }) => {
  const docs = [
    { label: "Conditions", href: item.conditions_doc },
    { label: "Contract", href: item.contract_doc },
    { label: "Policy", href: item.policy_doc },
  ].filter((doc) => doc.href);
  if (!docs.length) return <span className="text-muted">—</span>;
  return (
    <ul className="ip-doc-links">
      {docs.map((doc) => (
        <li key={doc.label}>
          <a
            href={doc.href}
            target="_blank"
            rel="noopener noreferrer"
            className="insurance-doc-link"
          >
            {doc.label}
          </a>
        </li>
      ))}
    </ul>
  );
};

const InsuranceProtectTable = ({
  data,
  total,
  currentPage,
  limit,
  onPageChange,
  onReviewRequested,
  onIssueRequested,
  onCaseAction,
  busyRowId,
}) => {
  const totalPages = Math.ceil(total / limit);
  const indexOfFirstItem = (currentPage - 1) * limit + 1;
  const indexOfLastItem = Math.min(currentPage * limit, total);

  const formatProperty = (property) => {
    if (!property) return "N/A";
    return (
      [property.street, property.street_number, property.muncipality]
        .filter(Boolean)
        .join(", ") || "N/A"
    );
  };

  const handleCopyPropertyId = async (propertyId) => {
    if (!propertyId) return;
    try {
      await navigator.clipboard.writeText(propertyId);
      toast.success("Property ID copied");
    } catch {
      toast.error("Failed to copy property ID");
    }
  };

  const [pendingAction, setPendingAction] = useState(null);

  const runConfirmedAction = (item, action) => {
    setPendingAction({ item, action });
  };

  const confirmPendingAction = () => {
    const { item, action } = pendingAction;
    setPendingAction(null);
    onCaseAction?.(item, action);
  };

  const renderPagination = () => {
    const items = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    items.push(
      <Pagination.First key="first" onClick={() => onPageChange(1)} disabled={currentPage === 1} />
    );
    items.push(
      <Pagination.Prev key="prev" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} />
    );
    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item key={page} active={page === currentPage} onClick={() => onPageChange(page)}>
          {page}
        </Pagination.Item>
      );
    }
    items.push(
      <Pagination.Next
        key="next"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages || totalPages === 0}
      />
    );
    items.push(
      <Pagination.Last
        key="last"
        onClick={() => onPageChange(totalPages)}
        disabled={currentPage === totalPages || totalPages === 0}
      />
    );

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted small">
          Showing <strong>{total > 0 ? indexOfFirstItem : 0}</strong> to{" "}
          <strong>{indexOfLastItem}</strong> of <strong>{total}</strong> records
        </div>
        <Pagination className="tenants-pagination mb-0">{items}</Pagination>
      </div>
    );
  };

  const renderPolicyDates = (item) => {
    const dates = item.policy_dates;
    if (!dates?.start) return <span className="fw-600">Not set yet</span>;
    return (
      <>
        <div>{formatDate(dates.start)}</div>
        {dates.end && <div>to {formatDate(dates.end)}</div>}
        {dates.requested && <div className="text-muted">requested start</div>}
      </>
    );
  };

  const renderPayment = (item) => {
    const badge = PAYMENT_BADGE[item.payment_status];
    if (!badge) return <span className="text-muted">—</span>;
    return (
      <>
        <span className={badge.className}>{badge.label}</span>
        {(item.payment_context || item.invoice_doc) && (
          <div className="ip-row-context">
            {item.payment_context}
            {item.payment_status === "paid" && item.invoice_doc && (
              <>
                {item.payment_context ? " · " : ""}
                <a
                  href={item.invoice_doc}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ip-context-link"
                >
                  invoice
                </a>
              </>
            )}
          </div>
        )}
        {/* Truust transaction: Ops searches it in Truust to refund. */}
        {item.transaction_id && (
          <div className="ip-row-context ip-txn-id" title="Truust transaction ID">
            txn {item.transaction_id}
          </div>
        )}
      </>
    );
  };

  // Automatic retries of MDP steps 4/5 within the hour, plus Ops retries.
  const renderRetries = (item) => {
    const retries = item.retries;
    if (!retries) return <span className="text-muted">—</span>;
    return (
      <>
        <div className="fw-600">
          {retries.automatic}/{retries.max} automatic
        </div>
        {retries.state === "retrying" && retries.next_attempt_at && (
          <div className="ip-row-context">
            next {moment(retries.next_attempt_at).format("HH:mm")}
          </div>
        )}
        {retries.last_attempt_at && (
          <div className="ip-row-context">
            last {moment(retries.last_attempt_at).format("DD MMM HH:mm")}
          </div>
        )}
        {retries.manual > 0 && (
          <div className="ip-row-context">
            {retries.manual} by Ops
          </div>
        )}
      </>
    );
  };

  const renderInsuranceStatus = (item) => {
    const badge = INSURANCE_STATUS_BADGE[item.insurance_status] || {
      label: item.insurance_status || "N/A",
      className: "insurance-payment-badge",
    };
    return (
      <>
        <span className={badge.className}>{badge.label}</span>
        {item.status_context && (
          <div className="ip-row-context">{item.status_context}</div>
        )}
        {item.status_detail && (
          <div className="ip-row-error" title={item.status_detail}>
            {item.status_detail}
          </div>
        )}
      </>
    );
  };

  const renderPendingAction = (item) => {
    const actions = item.actions || [];
    const isBusy = busyRowId === item._id;
    if (!actions.length) {
      return item.pending_note ? (
        <span className="ip-pending-note">{item.pending_note}</span>
      ) : (
        <span className="text-muted">—</span>
      );
    }

    return (
      <div className="ip-actions">
        <div className="ip-actions-buttons">
          {actions.includes("retry_issuance") && (
            <button
              type="button"
              className="ip-action-btn ip-action-btn-primary"
              disabled={isBusy}
              onClick={() => runConfirmedAction(item, "retry-issuance")}
            >
              {isBusy ? "Retrying…" : "Retry issuance"}
            </button>
          )}
          {actions.includes("upload_policy") && (
            <button
              type="button"
              className="ip-action-btn ip-action-btn-primary"
              disabled={isBusy}
              onClick={() => onIssueRequested?.(item)}
            >
              Upload policy
            </button>
          )}
          {actions.includes("mark_refunded") && (
            <button
              type="button"
              className="ip-action-btn ip-action-btn-primary"
              disabled={isBusy}
              onClick={() => runConfirmedAction(item, "refund")}
            >
              Mark refunded
            </button>
          )}
          {actions.includes("send_reminder") && (
            <button
              type="button"
              className="ip-action-btn"
              disabled={isBusy}
              onClick={() => runConfirmedAction(item, "send-reminder")}
            >
              Send reminder
            </button>
          )}
          {actions.includes("reject") && (
            <button
              type="button"
              className="ip-action-btn"
              disabled={isBusy}
              onClick={() => runConfirmedAction(item, "reject")}
            >
              Reject
            </button>
          )}
          {actions.includes("resolve_coverage_review") && (
            <button
              type="button"
              className="ip-action-btn ip-action-btn-primary"
              disabled={isBusy}
              onClick={() => onReviewRequested?.(item)}
            >
              Update policy
            </button>
          )}
        </div>
        {item.flagged_for_call && (
          <span className="ip-call-flag">Call the landlord</span>
        )}
        {actions.includes("open_mdp") && (
          <a
            href={item.mdp_url || "#"}
            target={item.mdp_url ? "_blank" : undefined}
            rel="noopener noreferrer"
            className="ip-context-link ip-mdp-link"
            onClick={(event) => {
              if (!item.mdp_url) event.preventDefault();
            }}
          >
            Open in MDP <ExternalLink size={12} />
          </a>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="table-responsive ip-table-scroll">
        <Table hover className="landlord-modal-table insurance-protect-table mb-0">
          <thead>
            <tr>
              <th style={{ width: "32px" }}>#</th>
              <th className="ip-product-col">PRODUCT</th>
              <th className="ip-project-col">PROJECT NO.</th>
              <th className="ip-property-col">PROPERTY</th>
              <th className="ip-tenants-col">TENANTS</th>
              <th className="ip-workspace-col">WORKSPACE</th>
              <th className="ip-beneficiary-col">BENEFICIARY</th>
              <th className="ip-policy-date-col">POLICY DATES</th>
              <th className="ip-premium-col">PREMIUM</th>
              <th className="ip-payment-status-col">PAYMENT</th>
              <th className="ip-status-col">INSURANCE STATUS</th>
              <th className="ip-retries-col">RETRIES</th>
              <th className="ip-docs-col">DOCS</th>
              <th className="ip-action-col">PENDING ACTION</th>
              <th className="text-nowrap">PROPERTY ID</th>
            </tr>
          </thead>
          <tbody>
            {data.length > 0 ? (
              data.map((item, index) => {
                const product = PRODUCT_BADGE[item.product] || {};
                const tenantNames = (item.tenants || [])
                  .map((t) => `${t.fname || ""} ${t.lname || ""}`.trim())
                  .filter(Boolean);

                return (
                  <tr key={item._id || index}>
                    <td className="text-muted" style={{ width: "32px" }}>
                      {indexOfFirstItem + index}
                    </td>
                    <td className="ip-product-col">
                      <span className={`product-badge ${product.className || ""}`}>
                        {product.label || "N/A"}
                      </span>
                    </td>
                    <td className="ip-project-col small fw-medium">
                      {item.project_number || (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td
                      className="fw-600 ip-property-col"
                      style={{ fontSize: "0.8rem", lineHeight: "1.4", wordBreak: "break-word" }}
                    >
                      {formatProperty(item.property)}
                    </td>
                    <td className="ip-tenants-col">
                      <div className="fw-600" style={{ fontSize: "0.8rem", lineHeight: "1.4", wordBreak: "break-word" }}>
                        {tenantNames.length > 0 ? tenantNames.join(", ") : "N/A"}
                      </div>
                    </td>
                    <td
                      className="fw-600 ip-workspace-col"
                      style={{ fontSize: "0.8rem", lineHeight: "1.4", wordBreak: "break-word" }}
                    >
                      {item.workspace?.name || "N/A"}
                    </td>
                    <td className="ip-beneficiary-col">
                      <div className="fw-600">{item.beneficiary?.label || "N/A"}</div>
                      {(item.beneficiary?.name || item.beneficiary?.note) && (
                        <div className="text-muted small">
                          {item.beneficiary.name || item.beneficiary.note}
                        </div>
                      )}
                    </td>
                    <td className="small fw-medium ip-policy-date-col">
                      {renderPolicyDates(item)}
                    </td>
                    <td className="fw-600 ip-premium-col">
                      {item.premium != null
                        ? `€ ${Number(item.premium).toLocaleString("es-ES", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`
                        : "N/A"}
                    </td>
                    <td className="ip-payment-status-col">{renderPayment(item)}</td>
                    <td className="ip-status-col">{renderInsuranceStatus(item)}</td>
                    <td className="ip-retries-col small">{renderRetries(item)}</td>
                    <td className="ip-docs-col">
                      <DocLinks item={item} />
                    </td>
                    <td className="ip-action-col">{renderPendingAction(item)}</td>
                    <td>
                      {item.property?._id && (
                        <button
                          type="button"
                          className="ip-copy-btn"
                          title="Copy property ID"
                          onClick={() => handleCopyPropertyId(item.property._id)}
                        >
                          <Copy size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={15} className="text-center py-5 text-muted">
                  No results found matching your filters
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      {totalPages > 1 && total > 0 && <div className="px-1">{renderPagination()}</div>}
      <ConfirmActionModal
        pending={pendingAction}
        onCancel={() => setPendingAction(null)}
        onConfirm={confirmPendingAction}
      />
    </>
  );
};

export default InsuranceProtectTable;
